/**
 * CR460: Match Rules editor section for AdapterFormModal.
 * CR460-P: Stable rule IDs, move up/down, duplicate, collapse-all, per-rule regex error index.
 */
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  Magnet,
  ArrowUp,
  ArrowDown,
  Copy,
  ChevronsDownUp,
  ChevronsUpDown,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MatchRule {
  id: string;
  enabled: boolean;
  priority: number;
  merchant_ids: string[];
  url_domains: string[];
  url_regex: string;
  notes: string;
  // transient UI state
  _regexError?: string;
}

interface Props {
  matchRulesEnabled: boolean;
  onMatchRulesEnabledChange: (v: boolean) => void;
  rules: MatchRule[];
  onRulesChange: (rules: MatchRule[]) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function validateRegex(pattern: string): string {
  if (!pattern.trim()) return "";
  try {
    new RegExp(pattern);
    return "";
  } catch (e) {
    return e instanceof Error ? e.message : "Invalid regex";
  }
}

function makeStableId(): string {
  return `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function makeEmptyRule(): MatchRule {
  return {
    id: makeStableId(),
    enabled: true,
    priority: 0,
    merchant_ids: [],
    url_domains: [],
    url_regex: "",
    notes: "",
  };
}

// ─── Chip input ──────────────────────────────────────────────────────────────

function ChipInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [input, setInput] = useState("");

  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput("");
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="h-7 text-xs font-mono"
        />
        <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={add}>Add</Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {values.map((v) => (
            <Badge key={v} variant="secondary" className="font-mono text-[10px] gap-1 pr-1">
              {v}
              <button onClick={() => onChange(values.filter((x) => x !== v))}>
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single rule row ──────────────────────────────────────────────────────────

function RuleRow({
  rule,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  collapsed,
  onToggleCollapse,
}: {
  rule: MatchRule;
  index: number;
  total: number;
  onChange: (rule: MatchRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const update = useCallback(
    (patch: Partial<MatchRule>) => onChange({ ...rule, ...patch }),
    [rule, onChange]
  );

  const handleRegexChange = (v: string) => {
    update({ url_regex: v, _regexError: validateRegex(v) });
  };

  const summaryParts: string[] = [];
  if (rule.merchant_ids.length) summaryParts.push(`${rule.merchant_ids.length} merchant_id(s)`);
  if (rule.url_domains.length) summaryParts.push(`${rule.url_domains.length} domain(s)`);
  if (rule.url_regex) summaryParts.push("regex");

  return (
    <div className={`rounded-md border bg-background ${!rule.enabled ? "opacity-60" : ""}`}>
      {/* Rule header */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none"
        onClick={onToggleCollapse}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <span className="text-xs font-medium text-muted-foreground shrink-0">Rule {index + 1}</span>
        {summaryParts.length > 0 && (
          <span className="text-[10px] text-muted-foreground truncate">
            — {summaryParts.join(", ")}
          </span>
        )}
        {rule._regexError && (
          <Badge variant="destructive" className="text-[9px] ml-auto shrink-0">regex error</Badge>
        )}
        <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Move up / down */}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onMoveUp}
            disabled={index === 0}
            title="Move up"
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onMoveDown}
            disabled={index === total - 1}
            title="Move down"
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
          {/* Duplicate */}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onDuplicate}
            title="Duplicate rule"
          >
            <Copy className="h-3 w-3" />
          </Button>
          <Switch
            checked={rule.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
            className="scale-75"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Rule body */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Priority</Label>
              <Input
                type="number"
                value={rule.priority}
                onChange={(e) => update({ priority: parseInt(e.target.value) || 0 })}
                className="h-7 text-xs w-24"
              />
            </div>
          </div>

          <ChipInput
            label="Merchant IDs"
            values={rule.merchant_ids}
            onChange={(v) => update({ merchant_ids: v })}
            placeholder="e.g. metro"
          />

          <ChipInput
            label="URL Domains"
            values={rule.url_domains}
            onChange={(v) => update({ url_domains: v })}
            placeholder="e.g. metro.ca"
          />

          <div className="space-y-1">
            <Label className="text-xs">URL Regex</Label>
            <Input
              value={rule.url_regex}
              onChange={(e) => handleRegexChange(e.target.value)}
              placeholder="e.g. metro\\.ca/en/flyer"
              className={`h-7 text-xs font-mono ${rule._regexError ? "border-destructive" : ""}`}
            />
            {rule._regexError && (
              <p className="text-[10px] text-destructive">{rule._regexError}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={rule.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Optional notes about this rule…"
              className="text-xs min-h-[48px]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MatchRulesEditor({ matchRulesEnabled, onMatchRulesEnabledChange, rules, onRulesChange }: Props) {
  // Track collapsed state per rule ID
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const addRule = () => {
    const newRule = makeEmptyRule();
    onRulesChange([...rules, newRule]);
  };

  const updateRule = (index: number, rule: MatchRule) => {
    const next = [...rules];
    next[index] = rule;
    onRulesChange(next);
  };

  const deleteRule = (index: number) => {
    const removed = rules[index];
    setCollapsedIds((prev) => { const s = new Set(prev); s.delete(removed.id); return s; });
    onRulesChange(rules.filter((_, i) => i !== index));
  };

  const moveRule = (from: number, to: number) => {
    if (to < 0 || to >= rules.length) return;
    const next = [...rules];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onRulesChange(next);
  };

  const duplicateRule = (index: number) => {
    const src = rules[index];
    const copy: MatchRule = { ...src, id: makeStableId() };
    const next = [...rules];
    next.splice(index + 1, 0, copy);
    onRulesChange(next);
  };

  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const collapseAll = () => setCollapsedIds(new Set(rules.map((r) => r.id)));
  const expandAll = () => setCollapsedIds(new Set());

  const hasRegexError = rules.some((r) => !!r._regexError);

  return (
    <div className="space-y-3">
      <Separator />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Magnet className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Match Rules</span>
          {rules.length > 0 && (
            <Badge variant="secondary" className="text-xs">{rules.length}</Badge>
          )}
          {hasRegexError && (
            <Badge variant="destructive" className="text-xs">regex error</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rules.length > 0 && (
            <>
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={collapseAll} title="Collapse all">
                <ChevronsDownUp className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={expandAll} title="Expand all">
                <ChevronsUpDown className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Switch
              checked={matchRulesEnabled}
              onCheckedChange={onMatchRulesEnabledChange}
              className="scale-75"
            />
            <span>Rules enabled</span>
          </div>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRule}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Rule
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Define rules to recommend this adapter for matching Merchant Sources. Suggestions are hints only — admins still apply mappings explicitly.
      </p>

      {rules.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-6 text-center">
          <p className="text-xs text-muted-foreground">No match rules defined.</p>
          <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={addRule}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add First Rule
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule, i) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              index={i}
              total={rules.length}
              onChange={(r) => updateRule(i, r)}
              onDelete={() => deleteRule(i)}
              onMoveUp={() => moveRule(i, i - 1)}
              onMoveDown={() => moveRule(i, i + 1)}
              onDuplicate={() => duplicateRule(i)}
              collapsed={collapsedIds.has(rule.id)}
              onToggleCollapse={() => toggleCollapse(rule.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Serialization helpers ────────────────────────────────────────────────────

export function rulesToJson(rules: MatchRule[]): unknown[] {
  return rules.map(({ _regexError: _err, ...r }) => ({
    ...r,
    merchant_ids: r.merchant_ids.length > 0 ? r.merchant_ids : undefined,
    url_domains: r.url_domains.length > 0 ? r.url_domains : undefined,
    url_regex: r.url_regex || undefined,
    notes: r.notes || undefined,
  }));
}

export function jsonToRules(json: unknown): MatchRule[] {
  if (!Array.isArray(json)) return [];
  return json.map((r: any) => ({
    id: r.id ?? makeStableId(),
    enabled: r.enabled ?? true,
    priority: r.priority ?? 0,
    merchant_ids: Array.isArray(r.merchant_ids) ? r.merchant_ids : [],
    url_domains: Array.isArray(r.url_domains) ? r.url_domains : [],
    url_regex: r.url_regex ?? "",
    notes: r.notes ?? "",
  }));
}

/** Returns true if any rule has a regex error */
export function hasMatchRuleErrors(rules: MatchRule[]): boolean {
  return rules.some((r) => !!r._regexError);
}

/** Returns indices of rules with regex errors (for per-rule error messages) */
export function matchRuleErrorIndexes(rules: MatchRule[]): number[] {
  return rules.reduce<number[]>((acc, r, i) => {
    if (r._regexError) acc.push(i);
    return acc;
  }, []);
}
